// src/pages/about/changelog/changelog-detail/ChangelogDetail.jsx
import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Box,
    Paper,
    Stack,
    Chip,
    Divider,
    Button,
} from "@mui/material";
import { Slide } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Transition untuk slide in dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Fungsi untuk format timestamp
const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
        const date = new Date(timestamp);
        return date.toLocaleString('id-ID', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    } catch (e) {
        return timestamp;
    }
};

// Fungsi untuk mendapatkan warna status
const getStatusColor = (status) => {
    switch (status) {
        case 'Published':
            return { bgcolor: '#e8f5e9', color: '#2e7d32' };
        case 'Draft':
            return { bgcolor: '#fff3e0', color: '#f57c00' };
        default:
            return { bgcolor: '#f5f5f5', color: '#616161' };
    }
};

// Fungsi untuk mendapatkan warna kategori
const getCategoryColor = (category) => {
    switch (category) {
        case 'New Feature':
            return { bgcolor: '#e3f2fd', color: '#1565c0' };
        case 'Update':
            return { bgcolor: '#f3e5f5', color: '#7b1fa2' };
        case 'Bug Fix':
            return { bgcolor: '#ffebee', color: '#c62828' };
        case 'Improvement':
            return { bgcolor: '#e8f5e9', color: '#2e7d32' };
        default:
            return { bgcolor: '#f5f5f5', color: '#616161' };
    }
};

// Fungsi untuk mendapatkan data user dari localStorage
const getStoredUserData = () => {
    try {
        // Coba cari key yang mengandung 'pineTracker'
        const keys = Object.keys(localStorage);
        for (const key of keys) {
            if (key.includes('pineTracker') || key.includes('user')) {
                const item = localStorage.getItem(key);
                if (item) {
                    const data = JSON.parse(item);
                    // Cek berbagai kemungkinan struktur
                    if (data?.data?.role) {
                        return data.data;
                    } else if (data?.role) {
                        return data;
                    } else if (data?.user?.role) {
                        return data.user;
                    }
                }
            }
        }
        
        // Jika tidak ditemukan dengan pattern, coba semua key
        for (const key of keys) {
            try {
                const item = localStorage.getItem(key);
                if (item && item.includes('developer')) {
                    const data = JSON.parse(item);
                    if (data?.role || data?.data?.role) {
                        return data.data || data;
                    }
                }
            } catch (e) {
                continue;
            }
        }
        
        return null;
    } catch (error) {
        console.error("Error getting stored user data:", error);
        return null;
    }
};

// Fungsi untuk memeriksa apakah user adalah developer
const isDeveloper = () => {
    const storedUserData = getStoredUserData();
    return storedUserData?.role?.toLowerCase() === 'developer';
};

function ChangelogDetail({ 
    open, 
    onClose, 
    changelog,
    onEdit,
    showEditButton = true,
    showBackButton = false,
    onBack
}) {
    if (!changelog) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                TransitionComponent={Transition}
                fullScreen
                maxWidth="lg"
            >
                <DialogTitle sx={{ 
                    m: 0, 
                    p: 2,
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f5f5f5'
                }}>
                    <Typography variant="h6" component="div">
                        Detail Changelog
                    </Typography>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    height: '100%',
                    backgroundColor: '#f5f5f5'
                }}>
                    <Typography variant="body1" color="text.secondary">
                        Data changelog tidak ditemukan
                    </Typography>
                </DialogContent>
            </Dialog>
        );
    }

    const statusColor = getStatusColor(changelog.status);
    const categoryColor = getCategoryColor(changelog.category);
    
    // Periksa apakah user adalah developer
    const developerMode = isDeveloper();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            fullScreen
            maxWidth="lg"
        >
            <DialogTitle sx={{ 
                m: 0, 
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f5f5f5'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {showBackButton && onBack && (
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={onBack}
                            aria-label="back"
                            sx={{ mr: 1 }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <Typography variant="h6" component="div">
                        Detail Changelog
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Hanya tampilkan tombol Edit jika user BUKAN developer */}
                    {showEditButton && onEdit && developerMode && (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => onEdit(changelog)}
                            sx={{ mr: 1 }}
                        >
                            Edit
                        </Button>
                    )}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ 
                backgroundColor: '#f5f5f5',
                p: 0,
                overflow: 'auto'
            }}>
                {/* Header Section */}
                <Paper sx={{ 
                    p: 3, 
                    mb: 2,
                    borderBottom: '2px solid #e0e0e0',
                    backgroundColor: 'white'
                }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {changelog.title || 'Untitled'}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                        </Box>
                        
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CategoryIcon fontSize="small" color="action" />
                                <Chip 
                                    label={changelog.category || 'General'}
                                    size="small"
                                    sx={{
                                        backgroundColor: categoryColor.bgcolor,
                                        color: categoryColor.color,
                                        fontWeight: 'medium'
                                    }}
                                />
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                    label={changelog.status || 'Draft'}
                                    size="small"
                                    sx={{
                                        backgroundColor: statusColor.bgcolor,
                                        color: statusColor.color,
                                        fontWeight: 'medium'
                                    }}
                                />
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                                <PersonIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    {changelog.user || 'Anonymous'}
                                </Typography>
                            </Box>
                        </Stack>
                        
                        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon fontSize="small" color="action" />
                                <Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        Tanggal & Waktu
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatTimestamp(changelog.datetime) || '-'}
                                    </Typography>
                                </Stack>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon fontSize="small" color="action" />
                                <Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        Dibuat Pada
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatTimestamp(changelog.createdAt) || '-'}
                                    </Typography>
                                </Stack>
                            </Box>
                            
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    ID
                                </Typography>
                                <Typography variant="body2" fontFamily="monospace">
                                    {changelog.id || '-'}
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                </Paper>
                
                {/* Description Section */}
                <Paper sx={{ 
                    p: 3, 
                    mb: 2,
                    backgroundColor: 'white'
                }}>
                    <Typography variant="h6" gutterBottom sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1,
                        mb: 3
                    }}>
                        <Box sx={{ 
                            width: 4, 
                            height: 24, 
                            backgroundColor: 'primary.main',
                            borderRadius: 1
                        }} />
                        Deskripsi
                    </Typography>
                    
                    <Box 
                        sx={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            p: 3,
                            backgroundColor: '#fafafa',
                            minHeight: 200,
                            overflow: 'auto',
                            '& h1': {
                                fontSize: '2em',
                                fontWeight: 'bold',
                                margin: '1em 0',
                                color: '#1976d2'
                            },
                            '& h2': {
                                fontSize: '1.5em',
                                fontWeight: 'bold',
                                margin: '0.83em 0',
                                color: '#1976d2'
                            },
                            '& h3': {
                                fontSize: '1.17em',
                                fontWeight: 'bold',
                                margin: '1em 0',
                                color: '#1976d2'
                            },
                            '& h4': {
                                fontSize: '1em',
                                fontWeight: 'bold',
                                margin: '1.33em 0',
                                color: '#1976d2'
                            },
                            '& p': {
                                margin: '0.5em 0',
                                lineHeight: 1.6
                            },
                            '& ul, & ol': {
                                paddingLeft: '1.5em',
                                margin: '0.5em 0',
                            },
                            '& li': {
                                margin: '0.25em 0',
                                lineHeight: 1.6
                            },
                            '& blockquote': {
                                borderLeft: '3px solid #ccc',
                                margin: '0.5em 0',
                                paddingLeft: '1em',
                                fontStyle: 'italic',
                                backgroundColor: '#f9f9f9',
                                padding: '0.5em 1em',
                                borderRadius: '0 4px 4px 0'
                            },
                            '& code': {
                                backgroundColor: '#f5f5f5',
                                padding: '0.2em 0.4em',
                                borderRadius: '3px',
                                fontFamily: 'monospace',
                                fontSize: '0.9em'
                            },
                            '& pre': {
                                backgroundColor: '#263238',
                                color: '#ffffff',
                                padding: '1em',
                                borderRadius: '5px',
                                overflow: 'auto',
                                fontFamily: 'monospace',
                                fontSize: '0.9em'
                            },
                            '& img': {
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: '4px',
                                margin: '1em 0'
                            },
                            '& a': {
                                color: '#1976d2',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                '&:hover': {
                                    color: '#1565c0'
                                }
                            },
                            '& mark': {
                                backgroundColor: '#fff3cd',
                                padding: '0.1em 0.2em',
                                borderRadius: '2px',
                            },
                            '& table': {
                                borderCollapse: 'collapse',
                                width: '100%',
                                margin: '1em 0'
                            },
                            '& th, & td': {
                                border: '1px solid #ddd',
                                padding: '8px',
                                textAlign: 'left'
                            },
                            '& th': {
                                backgroundColor: '#f2f2f2',
                                fontWeight: 'bold'
                            },
                            '& tr:nth-child(even)': {
                                backgroundColor: '#f9f9f9'
                            }
                        }}
                        dangerouslySetInnerHTML={{ __html: changelog.description || '<p>No description provided.</p>' }}
                    />
                    
                    {!changelog.description && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            minHeight: 200,
                            border: '1px dashed #e0e0e0',
                            borderRadius: 2,
                            backgroundColor: '#fafafa'
                        }}>
                            <Typography variant="body1" color="text.secondary">
                                Tidak ada deskripsi yang tersedia
                            </Typography>
                        </Box>
                    )}
                </Paper>
                
                {/* Additional Info Section */}
                <Paper sx={{ 
                    p: 3,
                    backgroundColor: 'white'
                }}>
                    <Typography variant="h6" gutterBottom sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1,
                        mb: 3
                    }}>
                        <Box sx={{ 
                            width: 4, 
                            height: 24, 
                            backgroundColor: 'secondary.main',
                            borderRadius: 1
                        }} />
                        Informasi Tambahan
                    </Typography>
                    
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Aksi
                            </Typography>
                            <Chip 
                                label={changelog.action || 'CREATE'}
                                size="small"
                                sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1565c0',
                                    fontWeight: 'medium'
                                }}
                            />
                        </Box>
                        
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Module
                            </Typography>
                            <Typography variant="body2">
                                {changelog.module || changelog.category || '-'}
                            </Typography>
                        </Box>
                        
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Details
                            </Typography>
                            <Typography variant="body2">
                                {changelog.details || changelog.title || '-'}
                            </Typography>
                        </Box>
                        
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Row Number
                            </Typography>
                            <Typography variant="body2" fontFamily="monospace">
                                {changelog.rowNumber || '-'}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
                
                {/* Footer Actions */}
                <Box sx={{ 
                    p: 3, 
                    display: 'flex', 
                    justifyContent: 'flex-end',
                    gap: 2,
                    backgroundColor: 'white',
                    borderTop: '1px solid #e0e0e0',
                    mt: 2
                }}>
                    {/* Hanya tampilkan tombol Edit jika user BUKAN developer */}
                    {showEditButton && onEdit && developerMode && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => onEdit(changelog)}
                        >
                            Edit Changelog
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={onClose}
                    >
                        Tutup
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default ChangelogDetail;